import {
  FC,
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { IconChevronDown } from "./icons";
import { useDismissable } from "./useDismissable";

export interface SelectOption {
  value: string;
  label: string;
}

/** Liste ile tetikleyici arası boşluk ve listenin en yüksek boyu (CSS ile aynı). */
const GAP = 6;
const LIST_MAX_H = 244;

/** Harfle arama tamponunun sıfırlanma süresi (native select ile aynı his). */
const TYPE_AHEAD_RESET_MS = 700;

type Variant = "default" | "page" | "col";

interface Props {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  id?: string;
  ariaLabel?: string;
  /** Ölçü varyantı: sayfalama seçicisi ve kolon araması daha küçüktür. */
  variant?: Variant;
}

const VARIANTS: Record<Variant, { wrap: string; trigger: string; icon: number }> = {
  default: { wrap: "", trigger: "sso-rd__select", icon: 15 },
  page: {
    wrap: " sso-rd__select-wrap--page",
    trigger: "sso-rd__select sso-rd__select--page",
    icon: 13,
  },
  col: {
    wrap: " sso-rd__select-wrap--col",
    trigger: "sso-rd__select sso-rd__select--col",
    icon: 12,
  },
};

interface Placement {
  left: number;
  width: number;
  top?: number;
  bottom?: number;
}

/** Tetikleyicinin ekrandaki yerinden listenin sabit konumunu hesaplar. */
function place(anchor: HTMLElement): Placement {
  const rect = anchor.getBoundingClientRect();
  const below = window.innerHeight - rect.bottom;

  // Altta yer kalmadıysa yukarı açılır: sayfalama seçicisi sayfanın en altında.
  if (below < LIST_MAX_H + GAP && rect.top > below) {
    return {
      left: rect.left,
      width: rect.width,
      bottom: window.innerHeight - rect.top + GAP,
    };
  }

  return { left: rect.left, width: rect.width, top: rect.bottom + GAP };
}

/**
 * Native `<select>` yerine düğme + listbox: native listeyi işletim sistemi çizdiği için
 * tasarımın panel dili ona uygulanamıyor. Liste `document.body`'ye portallanır, çünkü
 * kullanıldığı üç yerin de kırpan bir atası var.
 */
const Select: FC<Props> = ({
  value,
  onChange,
  options,
  id,
  ariaLabel,
  variant = "default",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  // Portal ilk açılışta takılır ve DOM'da kalır: kaldırılan bir eleman kapanış animasyonunu oynatamaz.
  const [isMounted, setIsMounted] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [placement, setPlacement] = useState<Placement | null>(null);

  const anchorRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const typeAhead = useRef("");
  const typeAheadTimer = useRef<number | undefined>(undefined);
  const listId = (id || "sso-rd-select") + "-list";

  const close = useCallback(() => setIsOpen(false), []);
  useDismissable(anchorRef, isOpen, close, listRef);

  const selectedIndex = options.findIndex((option) => option.value === value);
  const selectedLabel = selectedIndex >= 0 ? options[selectedIndex].label : "";
  const widestLabel = options.reduce(
    (widest, option) => (option.label.length > widest.length ? option.label : widest),
    ""
  );

  const open = () => {
    if (anchorRef.current) setPlacement(place(anchorRef.current));
    setHighlight(selectedIndex >= 0 ? selectedIndex : 0);
    setIsMounted(true);
    setIsOpen(true);
  };

  // Liste sabit konumlu; sayfa veya tablo kaydırılırsa tetikleyicisinden kopar.
  useEffect(() => {
    if (!isOpen) return;

    const onScroll = (event: Event) => {
      // Listenin kendi içindeki kaydırma onu kapatmamalı.
      if (listRef.current?.contains(event.target as Node)) return;
      setIsOpen(false);
    };
    const onResize = () => setIsOpen(false);

    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [isOpen]);

  useEffect(() => () => window.clearTimeout(typeAheadTimer.current), []);

  // Klavyeyle gezinirken vurgulanan seçenek görünür alanda tutulur.
  useEffect(() => {
    if (!isOpen || highlight < 0) return;
    const node = listRef.current?.children[highlight] as HTMLElement | undefined;
    node?.scrollIntoView({ block: "nearest" });
  }, [isOpen, highlight]);

  const pick = (option: SelectOption) => {
    onChange(option.value);
    setIsOpen(false);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "Escape" || event.key === "Tab") {
      setIsOpen(false);
      return;
    }

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!isOpen) {
        open();
        return;
      }
      const step = event.key === "ArrowDown" ? 1 : -1;
      setHighlight((current) => {
        const next = current + step;
        if (next < 0) return options.length - 1;
        if (next >= options.length) return 0;
        return next;
      });
      return;
    }

    if (event.key === "Home" || event.key === "End") {
      if (!isOpen) return;
      event.preventDefault();
      setHighlight(event.key === "Home" ? 0 : options.length - 1);
      return;
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (!isOpen) {
        open();
        return;
      }
      if (highlight >= 0 && highlight < options.length) pick(options[highlight]);
      return;
    }

    // Harfe basınca o harfle başlayan seçeneğe atlar; native `<select>` bunu yapıyordu.
    if (event.key.length === 1 && !event.ctrlKey && !event.altKey && !event.metaKey) {
      const needle = (typeAhead.current + event.key).toLowerCase();
      typeAhead.current = needle;
      window.clearTimeout(typeAheadTimer.current);
      typeAheadTimer.current = window.setTimeout(() => {
        typeAhead.current = "";
      }, TYPE_AHEAD_RESET_MS);

      const index = options.findIndex(
        (option) => option.label.toLowerCase().indexOf(needle) === 0
      );
      if (index < 0) return;

      event.preventDefault();
      if (!isOpen) {
        pick(options[index]);
        return;
      }
      setHighlight(index);
    }
  };

  const style = VARIANTS[variant];

  const list = (
    <div className="sso-rd__portal">
      <ul
        id={listId}
        ref={listRef}
        role="listbox"
        className={"sso-rd__select-list" + (isOpen ? " is-open" : "")}
        style={
          placement
            ? {
                left: placement.left,
                width: placement.width,
                top: placement.top,
                bottom: placement.bottom,
              }
            : undefined
        }
      >
        {options.map((option, index) => (
          <li key={option.value}>
            <button
              type="button"
              role="option"
              aria-selected={option.value === value}
              tabIndex={-1}
              className={
                "sso-rd__select-option" +
                (index === highlight ? " is-active" : "") +
                (option.value === value ? " is-selected" : "")
              }
              onMouseEnter={() => setHighlight(index)}
              onClick={() => pick(option)}
            >
              {option.label}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <div className={"sso-rd__select-wrap" + style.wrap} ref={anchorRef}>
      <button
        type="button"
        id={id}
        className={style.trigger}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listId}
        aria-label={ariaLabel}
        onClick={() => (isOpen ? setIsOpen(false) : open())}
        onKeyDown={onKeyDown}
      >
        <span className="sso-rd__select-value">{selectedLabel}</span>
        {/* Genişlik en uzun etikete sabitlenir; native select de öyle davranıyordu.
            Aksi halde seçim değişince kontrol enini değiştirip komşularını oynatır. */}
        <span className="sso-rd__select-sizer" aria-hidden="true">
          {widestLabel}
        </span>
      </button>

      <IconChevronDown size={style.icon} />

      {isMounted && createPortal(list, document.body)}
    </div>
  );
};

export default Select;
